import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactFlow, {
    ReactFlowProvider, addEdge,
    useNodesState, useEdgesState,
    useReactFlow, MiniMap, Controls, Background,
} from 'reactflow';
import 'reactflow/dist/base.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import TextNode   from '../flow/nodes/TextNode';
import ImageNode  from '../flow/nodes/ImageNode';
import AskNode    from '../flow/nodes/AskNode';
import ListNode   from '../flow/nodes/ListNode';
import APINode    from '../flow/nodes/APINode';
import FlowSidebar      from '../flow/FlowSidebar';
import FlowConfigPanel  from '../flow/FlowConfigPanel';
import GeneratePromptOverlay from '../flow/overlays/GeneratePromptOverlay';
import SaveFlowModal         from '../flow/overlays/SaveFlowModal';
import { chatbotUrl } from '../../util/config';

const defaultConfig = {
    mode: 'hybrid',
    rag_tags: [],
    language: 'en',
    channel: 'whatsapp',
    fallback_message: 'Let me connect you to an agent right away.',
};

const defaultListStructure = {
    header: '', question: '', footer: '',
    sections: [], items: [], variable: '', voicePrompt: '',
};

let nodeIdCounter = 2;
const getNewId = () => `node_${nodeIdCounter++}`;

const TYPE_MAP = {
    asktextnode: 'asktext', phonenode: 'phone', emailnode: 'email',
    datenode: 'date', numbernode: 'number', locnode: 'loc',
    urlnode: 'url', filenode: 'file',
};

const NODE_LABELS = {
    sendtextnode: 'Send Text', imagenode: 'Send Media',
    asktextnode: 'Ask Text', phonenode: 'Ask Phone', emailnode: 'Ask Email',
    datenode: 'Ask Date', numbernode: 'Ask Number', locnode: 'Ask Location',
    urlnode: 'Ask URL', filenode: 'Ask File', listnode: 'List Menu', apinode: 'API Connector',
};

const ASK_TYPES = new Set(['asktextnode','phonenode','emailnode','datenode','numbernode','locnode','urlnode','filenode']);

function normalizeNode(n) {
    const data = { ...(n.data || {}) };
    data.nodeType = data.nodeType || TYPE_MAP[n.type] || String(n.type).replace(/node$/, '');
    if (!data.validation) data.validation = { attempts: 1, regex: '', minLength: '', maxLength: '', errMessage: '', actionOnInvalid: 'skip' };
    return { ...n, data };
}

// ── API helper using fetch + chatbotUrl ───────────────────────────────────────
async function apiFetch(method, path, body) {
    const token = localStorage.getItem('token');
    const res = await fetch(`${chatbotUrl}${path}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.detail || data?.message || 'Request failed');
    return data;
}

// ── Inner canvas component ────────────────────────────────────────────────────
function FlowCanvas() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const flowId = searchParams.get('flow');
    const isNew  = searchParams.get('new') === 'true';

    const wrapperRef = useRef(null);
    const { setViewport } = useReactFlow();

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [rfInstance, setRfInstance] = useState(null);

    const [flowName,   setFlowName]   = useState('');
    const [config,     setConfig]     = useState(defaultConfig);
    const [showConfig, setShowConfig] = useState(false);

    const [selectedElements, setSelectedElements] = useState([]);
    const [nodesendText, setNodeSendText] = useState('');
    const [nodeImg,      setNodeImg]      = useState('');
    const [mediaType,    setMediaType]    = useState('image');
    const [mediaCaption, setMediaCaption] = useState('');
    const [nodeAskText,  setNodeAskText]  = useState(null);
    const [nodeList,     setNodeList]     = useState(defaultListStructure);
    const [nodeAPI,      setNodeAPI]      = useState({});
    const nodeAPIRef = useRef({});
    useEffect(() => { nodeAPIRef.current = nodeAPI; }, [nodeAPI]);

    const [showWelcome,   setShowWelcome]   = useState(isNew && !flowId);
    const [showGenerate,  setShowGenerate]  = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saving,  setSaving]  = useState(false);
    const [loading, setLoading] = useState(false);

    // Load existing flow
    useEffect(() => {
        if (!flowId) return;
        setLoading(true);
        apiFetch('GET', `/flows/${flowId}`)
            .then(res => {
                const f = res?.data || res;
                setFlowName(f.flow_name || '');
                setConfig({ ...defaultConfig, ...(f.config || {}) });
                const meta = f.metadata || {};
                setNodes((meta.nodes || []).map(normalizeNode));
                setEdges(meta.edges || []);
                if (meta.viewport) setViewport(meta.viewport);
                const maxId = Math.max(0, ...(meta.nodes || []).map(n => parseInt(n.id?.replace('node_', '')) || 0));
                nodeIdCounter = maxId + 1;
            })
            .catch(err => toast.error('Failed to load flow: ' + err.message))
            .finally(() => setLoading(false));
    }, [flowId]);

    // Sync selected node edits back to node data — debounced
    const syncTimeoutRef = useRef(null);
    useEffect(() => {
        if (!selectedElements.length) return;
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = setTimeout(() => {
            const sel = selectedElements[0];
            setNodes(nds => nds.map(n => {
                if (n.id !== sel.id) return n;
                const d = { ...n.data };
                if (sel.type === 'sendtextnode') d.sendText = nodesendText;
                else if (sel.type === 'imagenode') { d.imageUrl = nodeImg; d.mediatype = mediaType; d.mediaCaption = mediaCaption; }
                else if (ASK_TYPES.has(sel.type)) {
                    if (nodeAskText) { d.text = nodeAskText.text; d.variable = nodeAskText.variable; d.validation = nodeAskText.validation; }
                }
                else if (sel.type === 'listnode') d.map = nodeList;
                else if (sel.type === 'apinode') {
                    const apiVal = nodeAPIRef.current.api || nodeAPIRef.current;
                    d.api = { method: apiVal.method||'GET', url: apiVal.url||'', headers: apiVal.headers||[], queryParams: apiVal.queryParams||[], authentication: apiVal.authentication||{type:'none'}, body: apiVal.body||'', responseMapping: apiVal.responseMapping||[] };
                    d.retryCount = nodeAPIRef.current.retryCount ?? 1;
                    d.happyPathMessage = nodeAPIRef.current.happyPathMessage || '';
                    d.failoverMessage  = nodeAPIRef.current.failoverMessage  || '';
                }
                return { ...n, data: d };
            }));
        }, 300);
        return () => clearTimeout(syncTimeoutRef.current);
    }, [nodesendText, nodeImg, mediaType, mediaCaption, nodeAskText, nodeList]);

    const saveCurrentNode = useCallback(() => {
        if (!selectedElements.length) return;
        const sel = selectedElements[0];
        setNodes(nds => nds.map(n => {
            if (n.id !== sel.id) return n;
            const d = { ...n.data };
            if (sel.type === 'sendtextnode') d.sendText = nodesendText;
            else if (sel.type === 'imagenode') { d.imageUrl = nodeImg; d.mediatype = mediaType; d.mediaCaption = mediaCaption; }
            else if (ASK_TYPES.has(sel.type)) {
                if (nodeAskText) { d.text = nodeAskText.text; d.variable = nodeAskText.variable; d.validation = nodeAskText.validation; }
            }
            else if (sel.type === 'listnode') d.map = nodeList;
            else if (sel.type === 'apinode') {
                const apiVal = nodeAPIRef.current.api || nodeAPIRef.current;
                d.api = { method: apiVal.method||'GET', url: apiVal.url||'', headers: apiVal.headers||[], queryParams: apiVal.queryParams||[], authentication: apiVal.authentication||{type:'none'}, body: apiVal.body||'', responseMapping: apiVal.responseMapping||[] };
                d.retryCount = nodeAPIRef.current.retryCount ?? 1;
                d.happyPathMessage = nodeAPIRef.current.happyPathMessage || '';
                d.failoverMessage  = nodeAPIRef.current.failoverMessage  || '';
            }
            return { ...n, data: d };
        }));
    }, [selectedElements, nodesendText, nodeImg, mediaType, mediaCaption, nodeAskText, nodeList]);

    const onNodeClick = useCallback((_, node) => {
        setSelectedElements([node]);
        const d = node.data;
        if (node.type === 'sendtextnode') setNodeSendText(d.sendText || '');
        else if (node.type === 'imagenode') { setNodeImg(d.imageUrl||''); setMediaType(d.mediatype||'image'); setMediaCaption(d.mediaCaption||''); }
        else if (ASK_TYPES.has(node.type)) {
            setNodeAskText({ text: d.text||'', variable: d.variable||'', enabled: d.enabled||false, validation: d.validation || { attempts:1, regex:'', minLength:'', maxLength:'', errMessage:'', actionOnInvalid:'skip' } });
        }
        else if (node.type === 'listnode') setNodeList(d.map || defaultListStructure);
        else if (node.type === 'apinode') setNodeAPI({ ...(d.api || {}), retryCount: d.retryCount??1, happyPathMessage: d.happyPathMessage||'', failoverMessage: d.failoverMessage||'' });
        setNodes(nds => nds.map(n => ({ ...n, selected: n.id === node.id })));
    }, [setNodes]);

    const onConnect = useCallback((params) => {
        if (edges.some(e => e.source === params.source && e.sourceHandle === params.sourceHandle)) {
            toast.error('Source handle already connected'); return;
        }
        setEdges(eds => addEdge(params, eds));
    }, [edges, setEdges]);

    const onDragOver = useCallback(e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);

    const onDrop = useCallback(e => {
        e.preventDefault();
        const type = e.dataTransfer.getData('application/reactflow');
        if (!type || !wrapperRef.current || !rfInstance) return;
        const bounds = wrapperRef.current.getBoundingClientRect();
        const pos = rfInstance.project({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });
        const nodeType = TYPE_MAP[type] || type.replace('node', '');
        const sameTypeCount = nodes.filter(n => n.type === type).length;
        const nodeLabel = `${NODE_LABELS[type] || nodeType} ${sameTypeCount + 1}`;
        const newNode = {
            id: getNewId(), type, position: pos,
            data: {
                nodeType, label: nodeLabel,
                sendText: type === 'sendtextnode' ? 'Your message here' : undefined,
                imageUrl: type === 'imagenode' ? '' : undefined,
                mediatype: type === 'imagenode' ? 'image' : undefined,
                text: ASK_TYPES.has(type) ? '' : undefined,
                variable: ASK_TYPES.has(type) ? '' : undefined,
                validation: ASK_TYPES.has(type) ? { attempts:1, regex:'', minLength:'', maxLength:'', errMessage:'', actionOnInvalid:'skip' } : undefined,
                map: type === 'listnode' ? { ...defaultListStructure, items:[{ id:`itm_${Date.now()}`, label:'Option 1' },{ id:`itm_${Date.now()+1}`, label:'Option 2' }] } : undefined,
                api: type === 'apinode' ? { method:'GET', url:'', headers:[], queryParams:[], authentication:{type:'none'}, body:'', responseMapping:[], responseFormat:'json' } : undefined,
                retryCount: type === 'apinode' ? 1 : undefined,
                happyPathMessage: type === 'apinode' ? '' : undefined,
                failoverMessage:  type === 'apinode' ? '' : undefined,
            },
        };
        setNodes(nds => [...nds, newNode]);
    }, [rfInstance, setNodes, nodes]);

    const deleteSelectedNode = useCallback(() => {
        const sel = selectedElements[0];
        if (!sel) return;
        setNodes(n => n.filter(x => x.id !== sel.id));
        setEdges(e => e.filter(x => x.source !== sel.id && x.target !== sel.id));
        setSelectedElements([]);
    }, [selectedElements, setNodes, setEdges]);

    const handleGenerateResult = useCallback((result) => {
        const meta = result?.metadata || result?.flow || result;
        if (!meta) return;
        const ns = (meta.nodes || []).map(normalizeNode);
        setNodes(ns);
        setEdges(meta.edges || []);
        if (meta.config) setConfig(c => ({ ...c, ...meta.config }));
        const maxId = Math.max(0, ...ns.map(n => parseInt(n.id?.replace('node_', '')) || 0));
        nodeIdCounter = maxId + 1;
        setShowGenerate(false);
        toast.success(`Generated ${ns.length} nodes!`);
    }, [setNodes, setEdges]);

    const handleSave = async (name, cfg) => {
        if (nodes.length < 1) { toast.error('Add at least one node'); return; }
        setSaving(true);
        try {
            const flow = rfInstance ? rfInstance.toObject() : { nodes, edges, viewport: {} };
            const payload = {
                flow_name: name || flowName || 'Untitled Flow',
                config: cfg || config,
                metadata: { nodes: flow.nodes, edges: flow.edges, viewport: flow.viewport || {} },
            };
            if (flowId) {
                await apiFetch('PUT', `/flows/${flowId}`, payload);
                toast.success('Flow updated');
            } else {
                await apiFetch('POST', '/flows', payload);
                toast.success('Flow saved');
            }
            setShowSaveModal(false);
            setTimeout(() => navigate('/flows'), 1200);
        } catch (err) {
            toast.error(err.message || 'Failed to save flow');
        } finally {
            setSaving(false);
        }
    };

    const nodeTypes = useMemo(() => ({
        textnode: TextNode, sendtextnode: TextNode,
        imagenode: ImageNode,
        listnode: ListNode,
        asktextnode: AskNode, phonenode: AskNode, emailnode: AskNode,
        datenode: AskNode, numbernode: AskNode, locnode: AskNode,
        urlnode: AskNode, filenode: AskNode,
        apinode: APINode,
    }), []);

    if (loading) return (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:16 }}>
            <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTopColor:'#1565c0', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
            <p style={{ fontSize:13, color:'#6b7280' }}>Loading flow...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    return (
        <div style={{ display:'flex', flexDirection:'column', height:'100vh', background:'#f8fafc' }}>
            {/* Topbar */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 16px', height:52, background:'#fff', borderBottom:'1px solid #e2e8f0', flexShrink:0, zIndex:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <button onClick={() => navigate('/flows')}
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', fontSize:13, color:'#475569', background:'#f1f5f9', border:'none', borderRadius:8, cursor:'pointer' }}>
                        <i className="bi bi-arrow-left"></i> Back
                    </button>
                    <input value={flowName} onChange={e => setFlowName(e.target.value)}
                        onKeyDown={e => e.stopPropagation()}
                        placeholder="Flow name..."
                        style={{ padding:'6px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:14, fontWeight:600, color:'#0f172a', outline:'none', minWidth:240, background:'#f8fafc' }}/>
                    <div style={{ display:'flex', gap:4 }}>
                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:'#eff6ff', color:'#1565c0', fontWeight:600 }}>{config.mode}</span>
                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:'#f1f5f9', color:'#475569', fontWeight:600 }}>{nodes.length} nodes</span>
                    </div>
                </div>

                <div style={{ display:'flex', gap:8 }}>
                    <button onClick={() => setShowGenerate(true)}
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', fontSize:12, fontWeight:600, color:'#1565c0', background:'#eff6ff', border:'none', borderRadius:8, cursor:'pointer' }}>
                        <i className="bi bi-cpu"></i> AI Generate
                    </button>
                    <button onClick={() => setShowConfig(p => !p)}
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', fontSize:12, fontWeight:600, color:'#475569', border:'none', borderRadius:8, cursor:'pointer', background: showConfig ? '#e2e8f0':'#f1f5f9' }}>
                        <i className="bi bi-gear"></i> Config
                    </button>
                    <button onClick={() => setShowSaveModal(true)}
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 16px', fontSize:12, fontWeight:700, color:'#fff', background:'linear-gradient(to right, #2388e0, #1565c0)', border:'none', borderRadius:8, cursor:'pointer' }}>
                        <i className="bi bi-save"></i> Save Flow
                    </button>
                </div>
            </div>

            {/* Config panel */}
            {showConfig && (
                <div style={{ padding:'0 16px', background:'#fff', borderBottom:'1px solid #e2e8f0', maxHeight:400, overflowY:'auto' }}>
                    <FlowConfigPanel config={config} onChange={setConfig}/>
                </div>
            )}

            {/* Canvas */}
            <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
                <div style={{ flex:1, position:'relative' }} ref={wrapperRef}>
                    <ReactFlow
                        nodes={nodes} edges={edges}
                        nodeTypes={nodeTypes}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onInit={setRfInstance}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onNodeClick={onNodeClick}
                        onPaneClick={() => { setSelectedElements([]); setNodes(nds => nds.map(n => ({ ...n, selected:false }))); }}
                        deleteKeyCode={null}
                        fitView
                        style={{ background:'#f8fafc' }}
                    >
                        <Background variant="dots" gap={16} size={1} color="#cbd5e1"/>
                        <Controls position="top-left"/>
                        <MiniMap zoomable pannable position="bottom-right" nodeColor={() => '#1565c0'}/>
                    </ReactFlow>
                </div>

                <FlowSidebar
                    onDone={saveCurrentNode}
                    selectedNode={selectedElements[0]}
                    setSelectedElements={setSelectedElements}
                    nodesendText={nodesendText} setNodeSendText={setNodeSendText}
                    nodeImg={nodeImg} setNodeImg={setNodeImg}
                    mediaType={mediaType} setMediaType={setMediaType}
                    mediaCaption={mediaCaption} setMediaCaption={setMediaCaption}
                    nodeAskText={nodeAskText} setNodeAskText={setNodeAskText}
                    nodeList={nodeList} setNodeList={setNodeList}
                    nodeAPI={nodeAPI} setNodeAPI={setNodeAPI}
                    deleteSelectedNode={deleteSelectedNode}
                />
            </div>

            {/* Welcome overlay */}
            {showWelcome && (
                <div style={{ position:'fixed', inset:0, zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.5)' }}>
                    <div style={{ background:'#fff', borderRadius:20, padding:40, maxWidth:680, width:'100%', margin:'0 16px', boxShadow:'0 24px 80px rgba(0,0,0,0.3)', position:'relative' }}>
                        <button onClick={() => setShowWelcome(false)} style={{ position:'absolute', top:16, right:16, background:'none', border:'none', cursor:'pointer', color:'#64748b', fontSize:20 }}>×</button>
                        <h2 style={{ fontSize:24, fontWeight:700, color:'#111827', marginBottom:8 }}>Create Bot Flow</h2>
                        <p style={{ fontSize:14, color:'#6b7280', marginBottom:28 }}>Choose how to start building your conversation flow.</p>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                            {[
                                { icon:'bi-cpu', label:'Create with AI', desc:'Generate from a natural language prompt', color:'#1565c0', bg:'#eff6ff', action: () => { setShowWelcome(false); setShowGenerate(true); } },
                                { icon:'bi-layers', label:'Use Template', desc:'Start from a pre-built industry template', color:'#16a34a', bg:'#f0fdf4', action: () => setShowWelcome(false) },
                                { icon:'bi-plus-square', label:'From Scratch', desc:'Build manually with full control', color:'#d97706', bg:'#fffbeb', action: () => setShowWelcome(false) },
                            ].map(opt => (
                                <button key={opt.label} onClick={opt.action}
                                    style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12, padding:'24px 16px', background:opt.bg, border:`2px solid ${opt.color}22`, borderRadius:12, cursor:'pointer', transition:'all 0.2s' }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor=opt.color; e.currentTarget.style.transform='translateY(-2px)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor=`${opt.color}22`; e.currentTarget.style.transform='translateY(0)'; }}>
                                    <div style={{ width:48, height:48, borderRadius:12, background:opt.bg, border:`1px solid ${opt.color}33`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                        <i className={`bi ${opt.icon}`} style={{ fontSize:22, color:opt.color }}></i>
                                    </div>
                                    <div style={{ textAlign:'center' }}>
                                        <p style={{ fontWeight:700, fontSize:14, color:'#111827' }}>{opt.label}</p>
                                        <p style={{ fontSize:12, color:'#6b7280', marginTop:4 }}>{opt.desc}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showGenerate && (
                <GeneratePromptOverlay
                    onClose={() => setShowGenerate(false)}
                    onResult={handleGenerateResult}
                />
            )}

            {showSaveModal && (
                <SaveFlowModal
                    flowName={flowName}
                    config={config}
                    saving={saving}
                    onSave={handleSave}
                    onClose={() => setShowSaveModal(false)}
                />
            )}

            <ToastContainer />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

export default function FlowBuilder() {
    return (
        <ReactFlowProvider>
            <FlowCanvas/>
        </ReactFlowProvider>
    );
}
